/**
 * Cálculos del turno (diálogo de la liquidación).
 *
 * Reúne lo que el panel anterior repartía entre los botones *Detalles*,
 * *Resumen* y *Descuentos*: el cuadre completo del turno (concepto a concepto y
 * con los porcentajes que calculaba el modelo), los **totales por serie**, el
 * **desglose de descuentos** (documentos, créditos y otros, con sus sobrecargos) y
 * las estadísticas del turno (transacciones, volumen, precio medio, bóvedas,
 * gastos y lubricantes).
 */

import { Badge } from '@/components/ui/badge';
import { Dialog } from '@/components/ui/dialog';
import { formatCurrency, formatNumber, formatPaymentMethodName } from '@/lib/utils';

import type { TurnoLiquidacion } from '../reportes.api';

interface Props {
  turno: TurnoLiquidacion | null;
  onClose: () => void;
}

export function TurnoCalculosDialog({ turno, onClose }: Props) {
  const resumen = turno?.resumen_financiero;
  const series = (resumen?.desglose_series ?? []).filter((serie) => serie.cantidad > 0);
  const descuentos = resumen?.desglose_descuentos ?? [];
  const estadisticas = resumen?.estadisticas;

  const concepto = (etiqueta: string, valor: number, signo: 1 | -1 = 1, resaltar = false) => (
    <div
      key={etiqueta}
      className="flex items-baseline justify-between gap-3 border-b border-ink-100 py-1.5 last:border-0"
    >
      <span className={resaltar ? 'text-sm font-semibold text-ink-800' : 'text-sm text-ink-600'}>
        {etiqueta}
      </span>
      <span
        className={
          resaltar ? 'text-sm font-bold text-ink-900' : signo === -1 ? 'text-sm text-rose-600' : 'text-sm text-ink-700'
        }
      >
        {signo === -1 ? '− ' : ''}
        {formatCurrency(valor)}
      </span>
    </div>
  );

  return (
    <Dialog
      open={turno !== null}
      onClose={onClose}
      title="Cálculos del turno"
      description={
        turno
          ? `Turno #${turno.id} · ${turno.first_name ?? ''} ${turno.last_name ?? ''}`.trim()
          : undefined
      }
      size="xl"
    >
      {turno && resumen ? (
        <div className="grid gap-5 lg:grid-cols-2">
          <section className="space-y-1">
            <h3 className="pb-1 text-sm font-semibold text-ink-800">Cómo se forma el efectivo</h3>

            {concepto('Ventas de las transacciones', resumen.total_ventas_bruto)}
            {concepto('Lubricantes y extras', resumen.total_lubricantes)}
            {concepto('Ingresos adicionales', resumen.total_ingresos_adicionales)}
            {Number(turno.monto_gnv ?? 0) > 0 ? concepto('GNV', Number(turno.monto_gnv)) : null}
            {concepto('Bóvedas', resumen.total_bovedas, -1)}
            {concepto('Gastos', resumen.total_gastos, -1)}
            {resumen.metodos_pago_no_efectivo.map((metodo) =>
              concepto(formatPaymentMethodName(metodo.metodo), metodo.monto, -1),
            )}
            {concepto('Descuentos ventas directas', resumen.total_descuentos_sin_creditos, -1)}
            {concepto(
              'Ajuste por descuentos en créditos',
              -resumen.total_descuentos_creditos,
            )}
            {concepto('Vales de crédito (netos)', resumen.total_creditos, -1)}
            {resumen.total_consumo_interno > 0
              ? concepto('Consumo interno', resumen.total_consumo_interno, -1)
              : null}
            {resumen.total_canjes_cardplus > 0
              ? concepto('Canjes CardPlus', resumen.total_canjes_cardplus, -1)
              : null}
            {resumen.total_adelantados > 0
              ? concepto('Pagos adelantados', resumen.total_adelantados, -1)
              : null}
            {concepto('Efectivo esperado', resumen.efectivo_calculado, 1, true)}
            {concepto(
              resumen.efectivo_pendiente > 0
                ? 'Faltante'
                : resumen.efectivo_pendiente < 0
                  ? 'Sobrante'
                  : 'Cuadrado',
              Math.abs(resumen.efectivo_pendiente),
              1,
              true,
            )}

            <div className="mt-3 grid grid-cols-2 gap-2 rounded-card bg-ink-50/60 p-3 text-center">
              <div>
                <p className="text-[11px] text-ink-500 uppercase">Efectivo</p>
                <p className="text-sm font-semibold text-ink-900">
                  {formatNumber(resumen.porcentajes.efectivo)} %
                </p>
              </div>
              <div>
                <p className="text-[11px] text-ink-500 uppercase">Bóvedas</p>
                <p className="text-sm font-semibold text-ink-900">
                  {formatNumber(resumen.porcentajes.bovedas)} %
                </p>
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <div>
              <h3 className="pb-1 text-sm font-semibold text-ink-800">
                Totales por serie ({formatNumber(estadisticas?.total_transacciones ?? 0)} transacciones)
              </h3>
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                    <th className="px-2 py-1.5">Serie</th>
                    <th className="px-2 py-1.5 text-right">Trans.</th>
                    <th className="px-2 py-1.5 text-right">Volumen</th>
                    <th className="px-2 py-1.5 text-right">Monto</th>
                    <th className="px-2 py-1.5 text-right">%</th>
                  </tr>
                </thead>
                <tbody>
                  {series.length === 0 ? (
                    <tr className="border-t border-ink-100">
                      <td colSpan={5} className="px-2 py-4 text-center text-sm text-ink-500">
                        El turno no tiene transacciones.
                      </td>
                    </tr>
                  ) : null}

                  {series.map((serie) => (
                    <tr key={serie.codigo} className="border-t border-ink-100">
                      <td className="px-2 py-1.5 text-ink-800">
                        {serie.nombre} ({serie.codigo})
                      </td>
                      <td className="px-2 py-1.5 text-right text-ink-600">
                        {formatNumber(serie.cantidad)}
                      </td>
                      <td className="px-2 py-1.5 text-right text-ink-600">
                        {formatNumber(serie.volumen)}
                      </td>
                      <td className="px-2 py-1.5 text-right font-semibold text-ink-900">
                        {formatCurrency(serie.monto)}
                      </td>
                      <td className="px-2 py-1.5 text-right text-ink-500">
                        {formatNumber(serie.porcentaje)} %
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-ink-100 text-[13px] font-semibold text-ink-800">
                    <td className="px-2 py-1.5">Total</td>
                    <td className="px-2 py-1.5 text-right">
                      {formatNumber(estadisticas?.total_transacciones ?? 0)}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {formatNumber(estadisticas?.volumen_total ?? 0)}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {formatCurrency(resumen.total_ventas_bruto)}
                    </td>
                    <td className="px-2 py-1.5" />
                  </tr>
                </tfoot>
              </table>
            </div>

            <div>
              <h3 className="pb-1 text-sm font-semibold text-ink-800">Descuentos y sobrecargos</h3>
              {descuentos.length === 0 ? (
                <p className="text-sm text-ink-500">El turno no tiene descuentos ni recargos.</p>
              ) : (
                <table className="w-full border-collapse text-[13px]">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold tracking-wide text-ink-500 uppercase">
                      <th className="px-2 py-1.5">Origen</th>
                      <th className="px-2 py-1.5 text-right">Trans.</th>
                      <th className="px-2 py-1.5 text-right">Descuento</th>
                      <th className="px-2 py-1.5 text-right">Sobrecargo</th>
                      <th className="px-2 py-1.5 text-right">Neto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {descuentos.map((fila) => (
                      <tr key={fila.tipo} className="border-t border-ink-100">
                        <td className="px-2 py-1.5 text-ink-800">{fila.nombre}</td>
                        <td className="px-2 py-1.5 text-right text-ink-600">
                          {formatNumber(fila.cantidad)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-ink-700">
                          {formatCurrency(fila.total_descuento)}
                        </td>
                        <td className="px-2 py-1.5 text-right text-ink-700">
                          {formatCurrency(fila.total_sobrecargo)}
                        </td>
                        <td className="px-2 py-1.5 text-right font-semibold text-ink-900">
                          {formatCurrency(fila.neto)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-ink-100 text-[13px] font-semibold text-ink-800">
                      <td className="px-2 py-1.5">Total</td>
                      <td className="px-2 py-1.5" />
                      <td className="px-2 py-1.5 text-right">
                        {formatCurrency(resumen.total_descuentos_solo_positivos)}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {formatCurrency(resumen.total_sobrecargos)}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {formatCurrency(resumen.total_descuentos)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              <Badge variant="neutral">
                Precio medio {formatCurrency(estadisticas?.precio_promedio ?? 0)}
              </Badge>
              <Badge variant="neutral">
                Bóvedas {formatNumber(estadisticas?.cantidad_bovedas ?? 0)}
              </Badge>
              <Badge variant="neutral">
                Gastos {formatNumber(estadisticas?.cantidad_gastos ?? 0)}
              </Badge>
              <Badge variant="neutral">
                Lubricantes {formatNumber(estadisticas?.total_lubricantes_cantidad ?? 0)} en{' '}
                {formatNumber(estadisticas?.lineas_venta_lubricantes ?? 0)} línea(s)
              </Badge>
              {resumen.total_calibracion_galones > 0 ? (
                <Badge variant="warning">
                  Calibración {formatNumber(resumen.total_calibracion_galones)} gal
                </Badge>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}
    </Dialog>
  );
}
